import React, { useState, useEffect } from "react";
import {
    HiOutlineClipboardCheck,
    HiOutlinePaperAirplane,
    HiOutlineCheck,
    HiOutlineX,
    HiOutlineChatAlt,
    HiOutlineClock,
    HiOutlineExclamationCircle,
    HiOutlinePlus,
} from "react-icons/hi";
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    serverTimestamp,
    doc,
    updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useSelector } from "react-redux";
import useAdmin from "../../hooks/useAdmin";
import "./index.scss";



const PPCTesting = () => {
    const [feedbackTitle, setFeedbackTitle] = useState("");
    const [feedbackDescription, setFeedbackDescription] = useState("");
    const [feedbackCategory, setFeedbackCategory] = useState("bug");
    const [feedbackSeverity, setFeedbackSeverity] = useState("medium");
    const [feedbackModule, setFeedbackModule] = useState("general");
    const [feedbacks, setFeedbacks] = useState([]);
    const [allFeedbacks, setAllFeedbacks] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [adminResponse, setAdminResponse] = useState("");
    const [showAdminResponseModal, setShowAdminResponseModal] = useState(false);
    const [adminLoading, setAdminLoading] = useState(false);

    const { user } = useSelector((state) => state.auth);
    const { isAdmin } = useAdmin();

    // Fetch feedbacks (user's own or all for admin)
    useEffect(() => {
        const fetchFeedbacks = async () => {
            if (!user || !user.email) return;

            try {
                // Fetch user's own feedbacks
                const userQuery = query(
                    collection(db, "userTestingFeedbacks"),
                    where("userEmail", "==", user.email),
                    orderBy("createdAt", "desc")
                );

                const userSnapshot = await getDocs(userQuery);
                const userFeedbacksList = [];
                userSnapshot.forEach((doc) => {
                    userFeedbacksList.push({
                        id: doc.id,
                        ...doc.data(),
                    });
                });

                setFeedbacks(userFeedbacksList);

                // If admin, also fetch all feedbacks
                if (isAdmin) {
                    const allQuery = query(
                        collection(db, "userTestingFeedbacks"),
                        orderBy("createdAt", "desc")
                    );

                    const allSnapshot = await getDocs(allQuery);
                    const allFeedbacksList = [];
                    allSnapshot.forEach((doc) => {
                        allFeedbacksList.push({
                            id: doc.id,
                            ...doc.data(),
                        });
                    });

                    setAllFeedbacks(allFeedbacksList);
                }
            } catch (error) {
                console.error("Error fetching feedbacks:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFeedbacks();
    }, [user, isAdmin]);

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();

        if (!feedbackTitle.trim() || !feedbackDescription.trim()) {
            setSubmitError(
                "Please provide both a title and description for your feedback."
            );
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            await addDoc(collection(db, "userTestingFeedbacks"), {
                title: feedbackTitle,
                description: feedbackDescription,
                category: feedbackCategory,
                severity: feedbackSeverity,
                module: feedbackModule,
                status: "pending",
                userEmail: user.email,
                userName: user.displayName || user.email.split("@")[0],
                createdAt: serverTimestamp(),
                adminResponse: null,
                adminResponseDate: null,
            });

            // Success
            setSubmitSuccess(true);
            setFeedbackTitle("");
            setFeedbackDescription("");
            setFeedbackCategory("bug");
            setFeedbackSeverity("medium");
            setFeedbackModule("general");

            // Refetch feedbacks to show the newly added one
            const q = query(
                collection(db, "userTestingFeedbacks"),
                where("userEmail", "==", user.email),
                orderBy("createdAt", "desc")
            );

            const querySnapshot = await getDocs(q);
            const updatedFeedbacks = [];
            querySnapshot.forEach((doc) => {
                updatedFeedbacks.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setFeedbacks(updatedFeedbacks);

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSubmitSuccess(false);
                setShowFeedbackForm(false);
            }, 3000);
        } catch (error) {
            console.error("Error submitting feedback:", error);
            setSubmitError("Failed to submit feedback. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // Function to get severity badge color
    const getSeverityColor = (severity) => {
        switch (severity) {
            case "critical":
                return "critical";
            case "high":
                return "high";
            case "medium":
                return "medium";
            case "low":
                return "low";
            default:
                return "medium";
        }
    };

    // Function to get status badge color
    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "pending";
            case "in-progress":
                return "in-progress";
            case "resolved":
                return "resolved";
            case "rejected":
                return "rejected";
            default:
                return "pending";
        }
    };

    // Reset form when closing
    const handleCloseFeedbackForm = () => {
        setShowFeedbackForm(false);
        setFeedbackTitle("");
        setFeedbackDescription("");
        setFeedbackCategory("bug");
        setFeedbackSeverity("medium");
        setFeedbackModule("general");
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    // Admin functionality to respond to feedback
    const handleAdminResponse = (feedback) => {
        setSelectedFeedback(feedback);
        setAdminResponse(feedback.adminResponse || "");
        setShowAdminResponseModal(true);
    };

    // Submit admin response
    const handleSubmitAdminResponse = async (newStatus = null) => {
        if (!selectedFeedback) return;

        setAdminLoading(true);

        try {
            const feedbackRef = doc(db, "userTestingFeedbacks", selectedFeedback.id);
            const updateData = {
                adminResponse: adminResponse.trim(),
                adminResponseDate: serverTimestamp(),
                ...(newStatus && { status: newStatus })
            };

            await updateDoc(feedbackRef, updateData);

            // Update local state
            const updateFeedback = (feedback) => {
                if (feedback.id === selectedFeedback.id) {
                    return {
                        ...feedback,
                        adminResponse: adminResponse.trim(),
                        adminResponseDate: new Date(),
                        ...(newStatus && { status: newStatus })
                    };
                }
                return feedback;
            };

            setFeedbacks(prev => prev.map(updateFeedback));
            setAllFeedbacks(prev => prev.map(updateFeedback));

            // Close modal and reset
            setShowAdminResponseModal(false);
            setSelectedFeedback(null);
            setAdminResponse("");
        } catch (error) {
            console.error("Error updating feedback:", error);
        } finally {
            setAdminLoading(false);
        }
    };

    // Update feedback status
    const handleUpdateStatus = async (feedbackId, newStatus) => {
        try {
            const feedbackRef = doc(db, "userTestingFeedbacks", feedbackId);
            await updateDoc(feedbackRef, {
                status: newStatus,
                adminResponseDate: serverTimestamp()
            });

            // Update local state
            const updateFeedback = (feedback) => {
                if (feedback.id === feedbackId) {
                    return { ...feedback, status: newStatus };
                }
                return feedback;
            };

            setFeedbacks(prev => prev.map(updateFeedback));
            setAllFeedbacks(prev => prev.map(updateFeedback));
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };



    return (
        <div className="ppc-testing-page">
            <div className="page-header">
                <HiOutlineClipboardCheck className="header-icon" />
                <div className="header-content">
                    <h1>PPC Portal Feedback</h1>
                    <p>
                        Submit feedback, report issues, and track testing progress for the MyelinZ PPC Portal.
                    </p>
                </div>
            </div>

            <div className="ppc-testing-content">
                {isAdmin && (
                    <div className="admin-feedback-section">
                        <h2>All User Feedback</h2>
                        <div className="admin-feedbacks-grid">
                            {loading ? (
                                <div className="loading-feedbacks">
                                    <HiOutlineClock className="spinning" />
                                    <span>Loading all feedbacks...</span>
                                </div>
                            ) : allFeedbacks.length === 0 ? (
                                <div className="no-feedbacks">
                                    <p>No feedback submitted yet.</p>
                                </div>
                            ) : (
                                allFeedbacks.map((feedback) => (
                                    <div key={feedback.id} className="admin-feedback-card">
                                        <div className="feedback-header">
                                            <h3>{feedback.title}</h3>
                                            <div className="feedback-meta">
                                                <span className={`feedback-category ${feedback.category}`}>
                                                    {feedback.category}
                                                </span>
                                                {feedback.category === "bug" && (
                                                    <span className={`feedback-severity ${getSeverityColor(feedback.severity)}`}>
                                                        {feedback.severity}
                                                    </span>
                                                )}
                                                <span className={`feedback-status ${getStatusColor(feedback.status)}`}>
                                                    {feedback.status}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="feedback-body">
                                            <p><strong>User:</strong> {feedback.userName}</p>
                                            <p><strong>Module:</strong> {feedback.module}</p>
                                            <p>{feedback.description}</p>
                                        </div>

                                        {feedback.adminResponse && (
                                            <div className="admin-response">
                                                <div className="response-header">
                                                    <HiOutlineChatAlt />
                                                    <span>Admin Response</span>
                                                </div>
                                                <p>{feedback.adminResponse}</p>
                                            </div>
                                        )}

                                        <div className="admin-actions">
                                            <button
                                                className="respond-button"
                                                onClick={() => handleAdminResponse(feedback)}
                                            >
                                                <HiOutlineChatAlt />
                                                {feedback.adminResponse ? 'Update Response' : 'Respond'}
                                            </button>
                                            
                                            <select 
                                                value={feedback.status}
                                                onChange={(e) => handleUpdateStatus(feedback.id, e.target.value)}
                                                className="status-select"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="in-progress">In Progress</option>
                                                <option value="resolved">Resolved</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </div>

                                        <div className="feedback-footer">
                                            <span className="feedback-date">
                                                Submitted: {feedback.createdAt?.toDate().toLocaleDateString() || "Recently"}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div className="user-feedback-section">
                    <div className="feedback-header">
                        <h2>Your Feedback</h2>
                        <button
                            className="add-feedback-button"
                            onClick={() => setShowFeedbackForm(true)}
                        >
                            <HiOutlinePlus />
                            <span>Add Feedback</span>
                        </button>
                    </div>

                    <div className="feedback-history">
                        <h3>Your Feedback History</h3>

                        {loading ? (
                            <div className="loading-feedbacks">
                                <HiOutlineClock className="spinning" />
                                <span>Loading feedbacks...</span>
                            </div>
                        ) : feedbacks.length === 0 ? (
                            <div className="no-feedbacks">
                                <p>You haven't submitted any feedback yet.</p>
                            </div>
                        ) : (
                            <div className="feedbacks-list">
                                {feedbacks.map((feedback) => (
                                    <div
                                        key={feedback.id}
                                        className="feedback-item"
                                    >
                                        <div className="feedback-header">
                                            <h3>{feedback.title}</h3>
                                            <div className="feedback-meta">
                                                <span
                                                    className={`feedback-category ${feedback.category}`}
                                                >
                                                    {feedback.category}
                                                </span>

                                                {feedback.category ===
                                                    "bug" && (
                                                    <span
                                                        className={`feedback-severity ${getSeverityColor(
                                                            feedback.severity
                                                        )}`}
                                                    >
                                                        {feedback.severity}
                                                    </span>
                                                )}

                                                <span
                                                    className={`feedback-status ${getStatusColor(
                                                        feedback.status
                                                    )}`}
                                                >
                                                    {feedback.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="feedback-body">
                                            <p>{feedback.description}</p>
                                        </div>

                                        {feedback.adminResponse && (
                                            <div className="admin-response">
                                                <div className="response-header">
                                                    <HiOutlineChatAlt />
                                                    <span>Admin Response</span>
                                                </div>
                                                <p>{feedback.adminResponse}</p>
                                            </div>
                                        )}

                                        <div className="feedback-footer">
                                            <span className="feedback-date">
                                                Submitted:{" "}
                                                {feedback.createdAt
                                                    ?.toDate()
                                                    .toLocaleDateString() ||
                                                    "Recently"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Feedback Form Popup */}
            {showFeedbackForm && (
                <div className="feedback-popup-overlay">
                    <div className="feedback-popup">
                        <div className="popup-header">
                            <h2>Submit Feedback</h2>
                            <button
                                className="close-button"
                                onClick={handleCloseFeedbackForm}
                            >
                                <HiOutlineX />
                            </button>
                        </div>

                        <div className="popup-content">
                            <p>
                                Share your feedback, report issues, or suggest
                                improvements for the PPC Portal.
                            </p>

                            <form
                                className="feedback-form"
                                onSubmit={handleSubmitFeedback}
                            >
                                <div className="form-group">
                                    <label htmlFor="feedbackTitle">Title</label>
                                    <input
                                        type="text"
                                        id="feedbackTitle"
                                        value={feedbackTitle}
                                        onChange={(e) =>
                                            setFeedbackTitle(e.target.value)
                                        }
                                        placeholder="Brief description of your feedback"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="feedbackCategory">
                                        Category
                                    </label>
                                    <select
                                        id="feedbackCategory"
                                        value={feedbackCategory}
                                        onChange={(e) =>
                                            setFeedbackCategory(e.target.value)
                                        }
                                    >
                                        <option value="bug">Bug Report</option>
                                        <option value="feature">
                                            Feature Request
                                        </option>
                                        <option value="improvement">
                                            Improvement Suggestion
                                        </option>
                                        <option value="question">
                                            Question
                                        </option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="feedbackModule">
                                        Module
                                    </label>
                                    <select
                                        id="feedbackModule"
                                        value={feedbackModule}
                                        onChange={(e) =>
                                            setFeedbackModule(e.target.value)
                                        }
                                    >
                                        <option value="general">General</option>
                                        <option value="profile">Profile</option>
                                        <option value="documents">
                                            Documents
                                        </option>
                                        <option value="payslips">
                                            Payslips
                                        </option>
                                        <option value="tasks">
                                            Tasks & Performance
                                        </option>
                                        <option value="leave">
                                            Leave Management
                                        </option>
                                        <option value="claims">Claims</option>
                                        <option value="remote-work">
                                            Remote Work
                                        </option>
                                        <option value="admin">
                                            Admin Features
                                        </option>
                                    </select>
                                </div>

                                {feedbackCategory === "bug" && (
                                    <div className="form-group">
                                        <label htmlFor="feedbackSeverity">
                                            Severity
                                        </label>
                                        <select
                                            id="feedbackSeverity"
                                            value={feedbackSeverity}
                                            onChange={(e) =>
                                                setFeedbackSeverity(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">
                                                Medium
                                            </option>
                                            <option value="high">High</option>
                                            <option value="critical">
                                                Critical
                                            </option>
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="feedbackDescription">
                                        Description
                                    </label>
                                    <textarea
                                        id="feedbackDescription"
                                        value={feedbackDescription}
                                        onChange={(e) =>
                                            setFeedbackDescription(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Provide detailed information about your feedback"
                                        rows="5"
                                        required
                                    ></textarea>
                                </div>

                                {submitError && (
                                    <div className="error-message">
                                        <HiOutlineExclamationCircle />
                                        <span>{submitError}</span>
                                    </div>
                                )}

                                {submitSuccess && (
                                    <div className="success-message">
                                        <HiOutlineCheck />
                                        <span>
                                            Feedback submitted successfully!
                                        </span>
                                    </div>
                                )}

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={handleCloseFeedbackForm}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-button"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <HiOutlineClock className="spinning" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlinePaperAirplane />
                                                Submit Feedback
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Response Modal */}
            {showAdminResponseModal && selectedFeedback && isAdmin && (
                <div className="feedback-popup-overlay">
                    <div className="feedback-popup admin-response-popup">
                        <div className="popup-header">
                            <h2>Admin Response</h2>
                            <button
                                className="close-button"
                                onClick={() => {
                                    setShowAdminResponseModal(false);
                                    setSelectedFeedback(null);
                                    setAdminResponse("");
                                }}
                            >
                                <HiOutlineX />
                            </button>
                        </div>

                        <div className="popup-content">
                            <div className="feedback-summary">
                                <h3>{selectedFeedback.title}</h3>
                                <p><strong>User:</strong> {selectedFeedback.userName}</p>
                                <p><strong>Category:</strong> {selectedFeedback.category}</p>
                                <p><strong>Description:</strong> {selectedFeedback.description}</p>
                            </div>

                            <div className="admin-response-form">
                                <label htmlFor="adminResponse">Your Response</label>
                                <textarea
                                    id="adminResponse"
                                    value={adminResponse}
                                    onChange={(e) => setAdminResponse(e.target.value)}
                                    placeholder="Provide your response or feedback to the user"
                                    rows="5"
                                    required
                                />
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="cancel-button"
                                    onClick={() => {
                                        setShowAdminResponseModal(false);
                                        setSelectedFeedback(null);
                                        setAdminResponse("");
                                    }}
                                >
                                    Cancel
                                </button>
                                
                                <button
                                    className="submit-button"
                                    onClick={() => handleSubmitAdminResponse()}
                                    disabled={adminLoading || !adminResponse.trim()}
                                >
                                    {adminLoading ? (
                                        <>
                                            <HiOutlineClock className="spinning" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <HiOutlinePaperAirplane />
                                            Save Response
                                        </>
                                    )}
                                </button>

                                <button
                                    className="submit-button resolve-button"
                                    onClick={() => handleSubmitAdminResponse('resolved')}
                                    disabled={adminLoading || !adminResponse.trim()}
                                >
                                    {adminLoading ? (
                                        <>
                                            <HiOutlineClock className="spinning" />
                                            Resolving...
                                        </>
                                    ) : (
                                        <>
                                            <HiOutlineCheck />
                                            Resolve & Respond
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PPCTesting;
